import { type JSX, useCallback, useState } from 'react';
import { CheckIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { Button, Tooltip } from '@heroui/react';
import clsx from 'clsx';

interface CodeBlockProps {
  /** Code content to display */
  children: string;
  /** Optional language label (displayed in header) */
  language?: string;
  /** Additional CSS classes */
  className?: string;
  /** Show line numbers */
  showLineNumbers?: boolean;
  /** Title/filename for the code block */
  title?: string;
}

/**
 * CodeBlock - Styled code block with copy-to-clipboard functionality.
 * Displays code with optional language label and copy button.
 *
 * Future enhancement: Add syntax highlighting with Prism or Shiki.
 */
export function CodeBlock({
  children,
  className,
  language,
  showLineNumbers = false,
  title,
}: CodeBlockProps): JSX.Element {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = children;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [children]);

  const lines = children.split('\n');

  return (
    <div
      className={clsx(
        'relative rounded-lg bg-surface border border-default overflow-hidden my-4',
        className,
      )}
    >
      {/* Header with language/title and copy button */}
      <div className="flex items-center justify-between px-4 py-2 bg-surface-elevated border-b border-default">
        <div className="flex items-center gap-2">
          {title && <span className="text-xs font-medium text-foreground">{title}</span>}
          {language && !title && (
            <span className="text-xs font-mono text-muted uppercase">{language}</span>
          )}
          {language && title && (
            <span className="text-xs font-mono text-muted uppercase">({language})</span>
          )}
        </div>

        <Tooltip content={copied ? 'Copied!' : 'Copy to clipboard'}>
          <Button
            isIconOnly
            size="sm"
            variant="light"
            onPress={() => void handleCopy()}
            className="text-muted hover:text-foreground"
            aria-label={copied ? 'Copied' : 'Copy code'}
          >
            {copied ? (
              <CheckIcon className="h-4 w-4 text-success" />
            ) : (
              <ClipboardDocumentIcon className="h-4 w-4" />
            )}
          </Button>
        </Tooltip>
      </div>

      {/* Code content */}
      <div className="overflow-x-auto">
        <pre className="p-4 text-sm leading-relaxed">
          <code className="font-mono text-foreground">
            {showLineNumbers ? (
              <table className="w-full border-collapse">
                <tbody>
                  {lines.map((line, index) => (
                    <tr key={index}>
                      <td className="pr-4 text-muted text-right select-none w-8">{index + 1}</td>
                      <td className="whitespace-pre">{line}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              children
            )}
          </code>
        </pre>
      </div>
    </div>
  );
}
